import * as React from 'react';
import Slider, { createSliderWithTooltip, Range as SliderRange, } from 'rc-slider';
import {difference as _difference} from 'lodash';
import { requestAPIActions, } from 'loopback-react-request';

import { 
  WashBasinModel,
  TroughPositionProps,
  MinMaxModel,
  MinMaxCurrentModel,
  Configuration,
  PlateBuilder,
  AssemblyAccessoriesModel,
} from 'franke-trough-dependencies';

import { GlobalPrivateState, } from 'src/api';
import { connect } from 'react-redux';
import { handleWithTooltip } from 'src/app/Partials/Tooltips';
import DistancesFields from './DistancesFields';
import { WarningsTooltip } from '../../Warnings';
import { isConfigurationEditable } from 'src/redux/utils.redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons';
import MESSAGES from './BasinDimensions.messages';
import { FormattedMessage } from 'react-intl';
import ConsolesOverlapChoice from '../Assemblies/ConsoleOverlapChoice';

export interface DistancesProps {
  plateBuilder: GlobalPrivateState['plateBuilder'];
  configuration: GlobalPrivateState['configuration'];
  dispatch: Function;
  trough: GlobalPrivateState['trough'];
  warning: GlobalPrivateState['warning'];
}

export interface DistancesState {
  distances?: number[];
  spacing?: number;
}

export interface CalculationProps {
  center?: number[] | undefined;
  centerToCenter?: MinMaxCurrentModel[];
  defaults?: TroughPositionProps[];
  reqWidth?: number;
}

const Range = createSliderWithTooltip(SliderRange);

export class DistancesPartial extends React.Component<DistancesProps, DistancesState> {
  plateBuilderClass: PlateBuilder | undefined;

  constructor(props: DistancesProps) {
    super(props);
    this.state = {
      
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleRangeAfterChange = this.handleRangeAfterChange.bind(this);
    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.handleSlideAfterChange = this.handleSlideAfterChange.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
  }

  componentDidMount() {
    this.setPositionState();
  }

  componentDidUpdate(prevProps: DistancesProps) {
    if (!this.state.distances) {     
      this.setPositionState();
    }
    const configuration = this.props.configuration.result;
    const prevConfiguration = prevProps.configuration.result;

    if ((configuration && configuration.updated_at) !== (prevConfiguration && prevConfiguration.updated_at)) {
      this.setPositionState();
    }
    if (this.props.plateBuilder && !prevProps.plateBuilder) {
      this.setPositionState();
    }
  }

  public render() {
    const {
      distances,
      spacing,
    } = this.state;
    
    const plateWidth = this.washBasinDimension && this.washBasinDimension.plateWidth;

    return (
      <div className="partial-distances position-relative">
        {this.washBasinDimension && (
          <div className="form-group position-relative pb-2">
            {isConfigurationEditable() && (
              <WarningsTooltip itemKey="distances" />
            )}
            <ConsolesOverlapChoice id="distancesOverlap" />

            <div className="row">
              <div className="col-3 col-md-2 pr-0 text-left">
                0
              </div>
              <div className="col-6 col-md-8 p-0 pt-1">
                {spacing === 0 ? (
                  <>
                    {distances && this.minMaxOfSlide && (                          
                      <Slider 
                        disabled={this.disabled}
                        min={this.minMaxOfSlide.min}
                        max={this.minMaxOfSlide.max}
                        onAfterChange={this.handleSlideAfterChange}
                        onChange={this.handleSlideChange}
                        step={0.5}
                        value={distances[0]}
                        className={'rc-slider-troughed'}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {distances && spacing && this.minMaxOfSlide && (
                      <Range 
                        count={distances.length}
                        disabled={this.disabled}
                        min={this.minMaxOfSlide.min}
                        max={this.minMaxOfSlide.max}
                        onAfterChange={this.handleRangeAfterChange}
                        onChange={this.handleRangeChange}
                        step={0.5}
                        value={distances}
                        pushable={spacing === 0 ? false : spacing}
                        handle={handleWithTooltip}
                        className={'rc-slider-troughed'}
                      />
                    )}
                  </>
                )}
              </div>
              <div className="col-3 col-md-2 pl-0 text-right">
                {plateWidth}
              </div>
            </div>

            {distances && (
              <DistancesFields 
                disabled={this.disabled}
                onChange={this.handleInputChange}
              />
            )}

            <div className="row mt-4">
              <div className="col-9">
                <button className="arrowed-button" onClick={this.spaceEvenly}>
                  <FontAwesomeIcon icon={faLongArrowAltRight} /> 
                  <FormattedMessage {...MESSAGES.distancesPositionEvenly} />
                </button>
                <button className="arrowed-button" onClick={this.spaceSymmetrically}>
                  <FontAwesomeIcon icon={faLongArrowAltRight} /> 
                  <FormattedMessage {...MESSAGES.distancesPositionSymmetrically} />
                </button>
              </div>
              <div className="col-3 text-right">
                <small><FormattedMessage {...MESSAGES.distancesInmm} /></small>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  private get disabled() {
    const { warning } = this.props;
    return !isConfigurationEditable() || warning.requireReset.distances.required;
  }
  
  /*
  // This will make the troughs flush to the left/right... I'm leaving it here because they might ask for it, and the math is complex to me xP
  private spaceInbetween = () => {
    const configuration = this.props.configuration.result;

    if (this.state.distances && configuration && configuration.washBasinDimension && configuration.selectedTrough && this.minMaxOfSlide) {
      if (this.state.distances.length > 1) {
        const plateWidth = configuration.washBasinDimension.plateWidth;
        const troughWidth = configuration.selectedTrough.trough.reqWidth;
        const range = (plateWidth + troughWidth) - (troughWidth * 2);
        const distances = [...this.state.distances];
        const mean = range / (distances.length - 1);

        for (let i = -1; i < distances.length - 2; i++) {
          distances[i+1] = ((mean * (i + 1)) - (troughWidth / 2)) + troughWidth;
        }
        distances[0] = this.minMaxOfSlide.min;
        distances[distances.length-1] = this.minMaxOfSlide.max;

        this.dispatchConfiguration(distances);
      } else {
        this.spaceEvenly();
      }
    }
  }
  */


  private spaceEvenly = () => {
    const configuration = this.props.configuration.result;

    if (this.state.distances && configuration && configuration.washBasinDimension && configuration.selectedTrough) {
      const plateWidth = configuration.washBasinDimension.plateWidth;
      const distances = [...this.state.distances];
      const widthPart = plateWidth / distances.length;

      for (let i = 0; i < distances.length; i++) {
        distances[i] = Math.round((widthPart * i) + (widthPart / 2));
      }

      this.dispatchConfiguration(distances);
    }
  }

  private spaceSymmetrically = () => {
    const configuration = this.props.configuration.result;

    if (this.state.distances && configuration && configuration.washBasinDimension && configuration.selectedTrough) {
      const plateWidth = configuration.washBasinDimension.plateWidth;
      const troughWidth = configuration.selectedTrough.trough.reqWidth;
      const range = plateWidth + troughWidth;
      const distances = [...this.state.distances];
      const mean = range / (distances.length + 1);

      for (let i = 0; i < distances.length; i++) {
        distances[i] = Math.round(mean * (i + 1) - (troughWidth / 2));
      }

      this.dispatchConfiguration(distances);
    }
  }
 
  private dispatchConfiguration(values: number[]) {
    if (this.disabled || !this.washBasinDimension) { return; }

    const configuration = this.props.configuration.result;
    const washBasinDimension: WashBasinModel = this.washBasinDimension;
    washBasinDimension.distances = values;

    const assemblyAccessories = this.assemblyAccessories;

    if (assemblyAccessories) {
      assemblyAccessories.acceptedMiranitOverlap = false;
    }

    this.props.dispatch(requestAPIActions.load(
      'configuration', {
        url: `/Configurations/${configuration && configuration.id}`,
        options: {
          method: 'PATCH',
          data: {
            washBasinDimension,
            assemblyAccessories
          }
        },
      },
      true,
    ));
  }
  
  private get distances(): number[] | undefined {
    return this.washBasinDimension && this.washBasinDimension.distances;
  }

  private handleInputChange(distances: number []) {
    this.handleRangeAfterChange(distances);
  }

  private handleRangeAfterChange(values: number[]) {
    // values = values.map(v => Math.round(v));
    this.dispatchConfiguration(values);
  }

  private handleRangeChange(values: number[]) {
    // values = values.map(v => Math.round(v)); // Causing a bug whereby the trough ends up too close to the edge
  
    if (!values) { return; }
    
    const hasDifference = this.state.distances && _difference(values, this.state.distances).length > 0 ? true : false;
    if (!hasDifference) { return; }

    this.setState({
      distances: values,
    });
  }

  private handleSlideAfterChange(value: number) {
    this.handleRangeAfterChange([value]);
  }

  private handleSlideChange(value: number) {
    this.handleRangeChange([value]);
  }

  private get minMaxOfSlide(): MinMaxModel | undefined {
    if (!this.props.plateBuilder) {
      return;
    }

    const positionsRange = this.props.plateBuilder.calc.troughPositionsRange();

    return {
      min: positionsRange.min,
      max: positionsRange.max || 0,
    };
  }

  private setPositionState(): void {
    this.setState({
      distances: this.distances,
      spacing: this.troughQty === 1 || !this.props.plateBuilder ? 0 : this.props.plateBuilder.calc.troughToTroughWidth(),// dist && dist.reqWidth && dist.reqWidth, // Spacing determines how close troughs can get... does min width do the same?
    });
  }

  private get troughQty(): number {
    return this.props.configuration && this.props.configuration.result && this.props.configuration.result.selectedTrough && this.props.configuration.result.selectedTrough.qty || 1;
  }
 
  private get washBasinDimension(): WashBasinModel | undefined {
    const {
      configuration,
    } = this.props;

    return configuration && 
      configuration.result && 
      configuration.result.washBasinDimension ? 
        configuration.result.washBasinDimension : undefined;
  }
 
  private get assemblyAccessories(): AssemblyAccessoriesModel | undefined {
    const {
      configuration,
    } = this.props;

    return configuration && 
      configuration.result && 
      configuration.result.assemblyAccessories ? 
        configuration.result.assemblyAccessories : undefined;
  }
}

export const Distances = connect((state: GlobalPrivateState) => ({
  plateBuilder: state.plateBuilder,
  configuration: state.configuration,
  trough: state.trough,
  warning: state.warning,
}))(DistancesPartial);

export default Distances;